import { takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthMethods } from '@auth/models/auth-methods';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IOrganisationInvitationCreateForm, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { OrganisationUserService } from '@organisations/services/organisation-user/organisation-user.service';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { PhoneNumberControlComponent } from '@shared/components/forms/phone-number-control/phone-number-control.component';
import { RadioGroupControlComponent } from '@shared/components/forms/radio-group-control/radio-group-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { StepNavigatorComponent } from '@shared/components/ui/step-navigator/step-navigator.component';
import { AlertTypes } from '@shared/models/alert';
import { IRadioOption } from '@shared/models/form-controls';
import { IMobileNumber } from '@shared/models/numbers';
import { IStepNavigation, IStepNavigatorConfig, NavButtonStyle, StepDisplay, StepModes, StepUI } from '@shared/models/step-navigator';
import { ToastTypes } from '@shared/models/toast';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-add-user-modal',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		AlertComponent,
		StepNavigatorComponent,
		StandardControlComponent,
		PhoneNumberControlComponent,
		RadioGroupControlComponent,
	],
	templateUrl: './add-user-modal.component.html',
	styleUrl: './add-user-modal.component.scss',
})
export class AddUserModalComponent implements OnInit, OnDestroy {
	private activeModal = inject(NgbActiveModal);
	private toastService = inject(ToastService);
	private organisationUserService = inject(OrganisationUserService);
	private organisationSelectedService = inject(OrganisationSelectedService);

	destroyed$ = new EventEmitter<void>();
	isLoading = false;

	inviteUserForm: FormGroup<IOrganisationInvitationCreateForm>;
	OrganisationUserAccessLevel = OrganisationUserAccessLevel;
	AlertTypes = AlertTypes;
	organisationRef: DocumentReference;

	navigationConfig: IStepNavigatorConfig;
	selectedStep: IStepNavigation;
	AuthMethods = AuthMethods;

	accessLevelOptions: Array<IRadioOption> = [
		{ label: 'Owner', value: OrganisationUserAccessLevel.OWNER, id: OrganisationUserAccessLevel.OWNER },
		{ label: 'Employee', value: OrganisationUserAccessLevel.EMPLOYEE, id: OrganisationUserAccessLevel.EMPLOYEE },
	];

	ngOnInit(): void {
		this.inviteUserForm = new FormGroup<IOrganisationInvitationCreateForm>({
			email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
			phoneNumber: new FormControl<IMobileNumber | null>(null, [Validators.required]),
			accessLevel: new FormControl<OrganisationUserAccessLevel | null>(OrganisationUserAccessLevel.EMPLOYEE, [Validators.required]),
		});

		this.navigationConfig = {
			ui: StepUI.WIZARD,
			mode: StepModes.NG_SWITCH,
			displayType: StepDisplay.TABS,
			navButtonStyle: NavButtonStyle.CHEVRON,
			showStepChevrons: false,
			showStepNumbers: false,
			showNavButtons: false,
			steps: [
				{
					title: {
						full: 'Email',
						tabs: 'Email',
					},
					icon: 'bi-envelope-at',
					switchCase: AuthMethods.EMAIL,
				},
				{
					title: {
						full: 'Phone Number',
						tabs: 'Phone Number',
					},
					icon: 'bi-telephone',
					switchCase: AuthMethods.PHONE_NUMBER,
				},
			],
		};

		this.onStepChange(this.navigationConfig.steps[0]);

		this.subscrcibeToSelectedOrganisation();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	subscrcibeToSelectedOrganisation(): void {
		this.organisationSelectedService.selectedOrganisation.pipe(takeUntil(this.destroyed$)).subscribe((selectedOrg) => {
			if (selectedOrg) {
				this.organisationRef = selectedOrg?.organisationUser.organisation;
			} else {
				this.toastService.showToast({
					title: 'Error',
					message: 'No organisation selected.',
					type: ToastTypes.ERROR,
				});
			}
		});
	}

	onStepChange(step: IStepNavigation): void {
		this.selectedStep = step;
		if (step.switchCase === AuthMethods.EMAIL) {
			this.inviteUserForm.controls.phoneNumber.disable();
			this.inviteUserForm.controls.email.enable();
		} else if (step.switchCase === AuthMethods.PHONE_NUMBER) {
			this.inviteUserForm.controls.email.disable();
			this.inviteUserForm.controls.phoneNumber.enable();
		}
	}

	get email(): FormControl<string | null> {
		return this.inviteUserForm.controls.email;
	}

	get phoneNumber(): FormControl<IMobileNumber | null> {
		return this.inviteUserForm.controls.phoneNumber;
	}

	get accessLevel(): FormControl<OrganisationUserAccessLevel | null> {
		return this.inviteUserForm.controls.accessLevel;
	}

	closeModal(): void {
		this.activeModal.close();
	}

	submit(): void {
		if (this.inviteUserForm.invalid) {
			this.inviteUserForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;

		switch (this.selectedStep.switchCase) {
			case AuthMethods.PHONE_NUMBER:
				this.inviteUserByPhone();
				break;
			case AuthMethods.EMAIL:
				this.inviteUserByEmail();
				break;
		}
	}

	private inviteUserByEmail(): void {
		this.organisationUserService
			.inviteUserToOrganisationByEmail(this.email.value!, this.accessLevel.value!, this.organisationRef)
			.then((result) => {
				if (result) {
					this.handleSuccess(this.email.value!);
				} else {
					this.handleError();
				}
				this.isLoading = false;
			});
	}

	private inviteUserByPhone(): void {
		this.organisationUserService
			.inviteUserToOrganisationByPhone(this.phoneNumber.value!, this.accessLevel.value!, this.organisationRef)
			.then((result) => {
				if (result) {
					this.handleSuccess(this.phoneNumber.value!.internationalNumber!);
				} else {
					this.handleError();
				}
				this.isLoading = false;
			});
	}

	private handleSuccess(username: string): void {
		this.toastService.showToast({
			title: 'Organisation Invitation Success',
			message: `${username} invited to join your organisation`,
			type: ToastTypes.SUCCESS,
		});
		this.closeModal();
	}

	private handleError(): void {
		this.toastService.showToast({
			title: 'Invite failed',
			message:
				'This user cannot be invited. They may already be part of the organisation, have a pending invite, or the account does not exist.',
			type: ToastTypes.ERROR,
		});
	}

	get alertContent(): string {
		switch (this.accessLevel.value) {
			case OrganisationUserAccessLevel.OWNER:
				return 'Owners have full access to all functionalities of ServeOx. They are able to add new users to the platform.';
			case OrganisationUserAccessLevel.EMPLOYEE:
				return "The Employee user role is designed for company staff using ServeOx's platform. Employees cannot add users, view or edit settings, or access leads.";
			default:
				return '';
		}
	}
}
